/**
 * Unity WebGL 側の `VrmaHubBridge` GameObject との通信ラッパー。
 *
 * SendMessage は `SendMessage(gameObject, method, value?)` で string/number しか渡せない
 * ため、オブジェクトを渡す系のコマンドはこちらで JSON.stringify する。
 *
 * ハンドシェイク: Unity は起動後に `ready` イベントを emit する。それより前に送った
 * コマンドは Unity 側が警告ログして無視するため、キューイングは React（＝ここ）の責務。
 * `bridgeReady` が立つまでのコマンドは pendingCommands に積み、`ready` を受けた瞬間に
 * 順番通り flush する。
 *
 * ★罠（実ブラウザで踏んだ）: `Module.vrmaHubPutBytes` は Unity 側の
 * `VrmaHub_InstallCanvasHooks()`（`WebBridge.Start()` が `ready` を送る直前に必ず1回呼ぶ）
 * の中で初めて生える。つまり `vrmaHubPutBytes` の存在は **`ready` 受信後にしか保証されない**。
 * また取得元は `createUnityInstance` の呼び出し時に渡した config オブジェクトではなく、
 * `createUnityInstance` が resolve した `unityInstance`（の `Module`）から辿る必要がある。
 * Emscripten の `__postset` が触る内部の `Module` と、React が渡した config は別オブジェクト
 * で、config 側には生えないため（`TypeError: vrmaHubPutBytes is not a function` の原因）。
 * このため File 経由のアセット送信は、putBytes が使えるかどうかの判定自体を `ready` まで
 * 遅延させる（＝上記の pendingCommands / bridgeReady と同じ仕組みに乗せる）。判定を早めに
 * 行って「まだ無いから」と URL フォールバックに固定してしまうと、ready 後には使えるはずの
 * putBytes 経路を毎回逃してしまうため。
 *
 * イベント購読: Unity → JS は `Module.vrmaHubOnEvent(type, payload)` の1系統のみ。
 * 複数バックエンドインスタンスの同時稼働は無い（Unity インスタンス自体がシングルトン。
 * lib/unityViewer.ts 参照）ため、購読者は Set ではなく「常に最新の1つ」を保持する
 * モジュール変数で足りる。VrmaViewerUnity がマウント/アンマウントのたびに
 * setUnityEventListener で差し替える。
 */
import type { UnityInstance } from '../../lib/unityViewer';

export interface UnityReadyEvent {
  type: 'ready';
  payload: { bridgeVersion: string; unity: string; graphics: string; maxTexture: number };
}
export interface UnityLoadProgressEvent {
  type: 'loadProgress';
  payload: {
    reqId: string;
    kind: 'vrm' | 'vrma';
    phase: 'download' | 'parse' | 'build';
    loaded: number;
    total: number;
    ratio: number;
  };
}
export interface UnityVrmLoadedEvent {
  type: 'vrmLoaded';
  payload: {
    reqId: string;
    label: string;
    specVersion: string;
    height: number;
    meshCount: number;
    materialCount: number;
    textureCount: number;
    loadMs: number;
  };
}
export interface UnityVrmaLoadedEvent {
  type: 'vrmaLoaded';
  payload: { reqId: string; duration: number; hasExpressions: boolean; loadMs: number };
}
export interface UnityTimeEvent {
  type: 'time';
  payload: { elapsed: number; duration: number; playing: boolean };
}
export interface UnityPlayStateChangedEvent {
  type: 'playStateChanged';
  payload: { playing: boolean };
}
export interface UnityCameraEvent {
  type: 'camera';
  payload: { theta: number; phi: number; distance: number; target: [number, number, number] };
}
export interface UnityWarnEvent {
  type: 'warn';
  payload: { code: string; message: string };
}
export interface UnityErrorEvent {
  type: 'error';
  payload: { reqId?: string; code: string; message: string; detail?: string };
}
export interface UnityStatsEvent {
  type: 'stats';
  payload: { fps: number; triangles: number; drawCalls: number };
}

export type UnityEvent =
  | UnityReadyEvent
  | UnityLoadProgressEvent
  | UnityVrmLoadedEvent
  | UnityVrmaLoadedEvent
  | UnityTimeEvent
  | UnityPlayStateChangedEvent
  | UnityCameraEvent
  | UnityWarnEvent
  | UnityErrorEvent
  | UnityStatsEvent;

type UnityEventListener = (event: UnityEvent) => void;
let currentListener: UnityEventListener | null = null;

/** Unity イベントの購読者を差し替える。null を渡すと購読解除。 */
export function setUnityEventListener(listener: UnityEventListener | null): void {
  currentListener = listener;
}

let bridgeReady = false;

/**
 * 既に `ready` イベントを受信済みかどうかを問い合わせる。
 * Unity インスタンスはシングルトンで使い回されるため、`ready` はアプリのライフタイムで
 * 最初の起動時に一度しか飛んでこない。ページ遷移で VrmaPreview が再マウントされても
 * Unity 自体は起動済みのままなので、再マウント直後の初期化に使う（VrmaPreview.tsx の
 * unityReady の初期値参照）。
 */
export function isUnityBridgeReady(): boolean {
  return bridgeReady;
}

let pendingCommands: Array<() => void> = [];

function sendCommand(instance: UnityInstance, method: string, value?: string | number): void {
  const run = () => instance.SendMessage('VrmaHubBridge', method, value);
  if (bridgeReady) run();
  else pendingCommands.push(run);
}

function flushPendingCommands(): void {
  const queued = pendingCommands;
  pendingCommands = [];
  queued.forEach((fn) => fn());
}

// reqId ごとの「ロード完了 or エラーで呼ぶ後始末」コールバック（File→objectURL の revoke 用）。
const settledCallbacks = new Map<string, () => void>();

function registerSettledCallback(reqId: string, cb?: () => void): void {
  if (cb) settledCallbacks.set(reqId, cb);
}

function resolveSettled(reqId: string | undefined): void {
  if (!reqId) return;
  const cb = settledCallbacks.get(reqId);
  if (cb) {
    settledCallbacks.delete(reqId);
    cb();
  }
}

// putBytes 未使用（URL フォールバック）を知らせる console.warn は初回のみ出す
// （File を選ぶたびに毎回警告が積み重なると、ノイズになって本当に見るべきログが埋もれるため）。
let putBytesFallbackWarned = false;
function warnPutBytesFallbackOnce(): void {
  if (putBytesFallbackWarned) return;
  putBytesFallbackWarned = true;
  console.warn(
    '[unityBridge] Module.vrmaHubPutBytes が利用できないため、URL (objectURL) 経由にフォールバックします。',
  );
}

/**
 * Unity 側 `.jslib` が `Module.vrmaHubOnEvent(type, payload)` として呼ぶ入口。
 * payload は素の JS オブジェクトを想定するが、文字列で来た場合（JSON 文字列化されて
 * いる場合）にも耐えるよう防御的に JSON.parse を試みる。
 */
export function vrmaHubOnEvent(type: string, rawPayload: unknown): void {
  let payload: unknown = rawPayload;
  if (typeof rawPayload === 'string') {
    try {
      payload = JSON.parse(rawPayload);
    } catch {
      // JSON でなければ文字列のまま渡す。
    }
  }

  if (type === 'ready') {
    bridgeReady = true;
    flushPendingCommands();
  }
  if (
    (type === 'vrmLoaded' || type === 'vrmaLoaded' || type === 'error') &&
    payload &&
    typeof payload === 'object'
  ) {
    resolveSettled((payload as { reqId?: string }).reqId);
  }

  currentListener?.({ type, payload } as UnityEvent);
}

let reqCounter = 0;
function nextReqId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  reqCounter += 1;
  return `req-${reqCounter}`;
}

// ---- ビューポート・テーマ・品質 ----

export function setViewport(instance: UnityInstance, cssWidth: number, cssHeight: number, dpr: number): void {
  sendCommand(instance, 'Cmd_SetViewport', JSON.stringify({ cssWidth, cssHeight, dpr }));
}

export interface UnityThemeColors {
  background: string;
  gridMajor: string;
  gridMinor: string;
  shadowTint: string;
  shadowStrength: string;
}

export function setTheme(instance: UnityInstance, theme: UnityThemeColors): void {
  sendCommand(instance, 'Cmd_SetTheme', JSON.stringify(theme));
}

export function setQuality(instance: UnityInstance, quality: 'low' | 'medium' | 'high'): void {
  sendCommand(instance, 'Cmd_SetQuality', quality);
}

export function setTargetFps(instance: UnityInstance, fps: number): void {
  sendCommand(instance, 'Cmd_SetTargetFps', Math.round(fps));
}

export function setInputEnabled(instance: UnityInstance, enabled: boolean): void {
  sendCommand(instance, 'Cmd_SetInputEnabled', enabled ? 1 : 0);
}

export function disposeBridge(instance: UnityInstance): void {
  sendCommand(instance, 'Cmd_Dispose');
}

// ---- アセット読み込み ----

export interface LoadAssetOptions {
  label?: string;
  autoPlay?: boolean;
  /** ロード完了（vrmLoaded/vrmaLoaded）またはエラーで一度だけ呼ばれる。 */
  onSettled?: () => void;
}

/**
 * File 経由のロード本体。putBytes が使えるかどうかの判定を含めて `ready` 後まで遅延させる
 * ため、sendCommand と同じ pendingCommands / bridgeReady に相乗りする（呼び出し側の
 * sendAssetToUnity から見ると、bridgeReady なら即実行、そうでなければキューに積まれる）。
 *
 * putBytes は「存在しない」（未定義）だけでなく「呼んだら例外を投げる」可能性も型では
 * 排除できないため、`typeof === 'function'` のガードと try/catch の両方で守り、いずれの
 * 場合も objectURL の URL 経路へフォールバックする（実ブラウザで動作確認済みの安全な経路）。
 */
function loadFileAsset(
  instance: UnityInstance,
  method: string,
  reqId: string,
  source: File,
  label: string,
  autoPlay: boolean,
  onSettled?: () => void,
): void {
  // 実行時点で bridgeReady は既に true（loadFileAsset 自体が bridgeReady/pendingCommands で
  // ready 後まで遅延されている）だが、送信経路は既存の sendCommand に揃えて重複させない。
  const fallbackToUrl = () => {
    const url = URL.createObjectURL(source);
    registerSettledCallback(reqId, () => {
      URL.revokeObjectURL(url);
      onSettled?.();
    });
    sendCommand(instance, method, JSON.stringify({ reqId, url, label, handle: 0, autoPlay }));
  };

  // ready 後に評価するので、config ではなく resolve 済みの unityInstance.Module から辿る
  // （config 経由では生えない。ファイル冒頭のコメント参照）。
  const putBytes = instance.Module?.vrmaHubPutBytes;
  if (typeof putBytes !== 'function') {
    warnPutBytesFallbackOnce();
    fallbackToUrl();
    return;
  }

  source
    .arrayBuffer()
    .then((buffer) => {
      const handle = putBytes(buffer);
      registerSettledCallback(reqId, onSettled);
      sendCommand(instance, method, JSON.stringify({ reqId, url: '', label, handle, autoPlay }));
    })
    .catch((err) => {
      console.error('[unityBridge] vrmaHubPutBytes の呼び出しに失敗しました。', err);
      warnPutBytesFallbackOnce();
      fallbackToUrl();
    });
}

/**
 * VRM/VRMA を Unity に読み込ませる。source が string（URL・blob URL）なら URL 経路。
 * File の場合は Module.vrmaHubPutBytes が使えれば handle 経路、使えなければ
 * URL.createObjectURL で URL 経路にフォールバックする。フォールバック時に作った
 * objectURL は、ロード完了 or エラー（vrmLoaded/vrmaLoaded/error のいずれか）を
 * 待ってから revoke する。
 *
 * File の場合、putBytes が使えるかの判定自体を `ready` まで遅延させる（loadFileAsset 参照）。
 * reqId は同期的に確定させて即座に返すので、呼び出し側の `await` は「Unity に送信済み」を
 * 待つものではない点は string 経路と同じ（元々 URL 経路も送信完了を待っていない）。
 */
export async function sendAssetToUnity(
  instance: UnityInstance,
  kind: 'vrm' | 'vrma',
  source: string | File,
  opts: LoadAssetOptions = {},
): Promise<string> {
  const reqId = nextReqId();
  const method = kind === 'vrm' ? 'Cmd_LoadVrm' : 'Cmd_LoadVrma';
  const autoPlay = opts.autoPlay ?? true;

  if (typeof source === 'string') {
    const label = opts.label ?? source;
    registerSettledCallback(reqId, opts.onSettled);
    sendCommand(instance, method, JSON.stringify({ reqId, url: source, label, handle: 0, autoPlay }));
    return reqId;
  }

  const label = opts.label ?? source.name;
  const run = () => loadFileAsset(instance, method, reqId, source, label, autoPlay, opts.onSettled);
  if (bridgeReady) run();
  else pendingCommands.push(run);
  return reqId;
}

export function clearVrma(instance: UnityInstance): void {
  sendCommand(instance, 'Cmd_ClearVrma');
}

export function cancelLoad(instance: UnityInstance, reqId: string): void {
  sendCommand(instance, 'Cmd_CancelLoad', reqId);
}

// ---- 再生制御 ----

export function setPlaying(instance: UnityInstance, playing: boolean): void {
  sendCommand(instance, 'Cmd_SetPlaying', playing ? 1 : 0);
}

export function seek(instance: UnityInstance, seconds: number): void {
  sendCommand(instance, 'Cmd_Seek', seconds);
}

export function setSpeed(instance: UnityInstance, speed: number): void {
  sendCommand(instance, 'Cmd_SetSpeed', speed);
}

export function setLoop(instance: UnityInstance, loop: boolean): void {
  sendCommand(instance, 'Cmd_SetLoop', loop ? 1 : 0);
}

export function setSeeking(instance: UnityInstance, seeking: boolean): void {
  sendCommand(instance, 'Cmd_SetSeeking', seeking ? 1 : 0);
}

// ---- カメラ操作 ----
// dTheta/dPhi・dx/dy は wire 互換のためのフィールド名で、実体は CSS ピクセル差分。

export function orbit(instance: UnityInstance, dTheta: number, dPhi: number): void {
  sendCommand(instance, 'Cmd_Orbit', JSON.stringify({ dTheta, dPhi }));
}

export function pan(instance: UnityInstance, dx: number, dy: number): void {
  sendCommand(instance, 'Cmd_Pan', JSON.stringify({ dx, dy }));
}

export function dolly(instance: UnityInstance, scale: number): void {
  sendCommand(instance, 'Cmd_Dolly', scale);
}

export function resetCameraCommand(instance: UnityInstance): void {
  sendCommand(instance, 'Cmd_ResetCamera');
}

export interface UnityCameraPose {
  theta: number;
  phi: number;
  distance: number;
  target: [number, number, number];
}

export function setCameraPose(instance: UnityInstance, pose: UnityCameraPose): void {
  sendCommand(instance, 'Cmd_SetCameraPose', JSON.stringify(pose));
}
